import type {
  IDateFilterParams,
  INumberFilterParams,
  ISetFilterParams,
  ITextFilterParams,
} from 'ag-grid-community';
import { XDragonflyNoDataText } from 'xsolis-component-library';

import { PediatricIndicatorValues } from '@/features/common/constants';

import { filterDateComparator } from './utils/filterDateComparator';

export const defaultTextFilterParams: ITextFilterParams = {
  buttons: ['reset'],
  debounceMs: 500,
  defaultOption: 'contains',
  filterOptions: ['contains'],
  maxNumConditions: 1,
};

export const defaultNumberFilterParams: INumberFilterParams = {
  buttons: ['reset'],
  debounceMs: 500,
  defaultOption: 'equals',
  filterOptions: ['equals'],
  maxNumConditions: 1,
};

export const defaultDateFilterParams: IDateFilterParams = {
  buttons: ['reset'],
  comparator: filterDateComparator,
  debounceMs: 500,
  filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange', 'blank'],
  inRangeInclusive: true,
  maxNumConditions: 1,
};

export const defaultSetFilterParams: ISetFilterParams = {
  buttons: ['reset'],
  debounceMs: 500,
};

export const pediatricIndicatorFilterParams: ISetFilterParams = {
  ...defaultSetFilterParams,
  excelMode: 'mac',
  suppressMiniFilter: true,
  values: [PediatricIndicatorValues.UNDER_18, PediatricIndicatorValues.OVER_18],
};

export const defaultSideBarTableProps = {
  toolPanels: [
    {
      iconKey: 'columns',
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressPivotMode: true,
        suppressPivots: true,
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
  ],
};

export const NO_DATA_AVAILABLE = 'No data available';
export const TABLE_LOADING_MESSAGE = 'Loading...';

export const COMPACT_ROW_HEIGHT = 50;
export const NARROW_ROW_HEIGHT = 32;

export const DEFAULT_MAX_REQUESTS = 2;
export const ROWS_PER_REQUEST = 50;

export const PAGINATION_PAGE_SIZE = 100;
export const PAGINATION_AVAILABLE_PAGE_SIZES = [5, 10, 20, 25, 50, 100];

export const defaultBaseTableProps = {
  defaultColDef: {
    flex: 1,
    menuTabs: [],
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  enableCellTextSelection: true,
  noRowsOverlayComponent: XDragonflyNoDataText,
  overlayLoadingTemplate: TABLE_LOADING_MESSAGE,
  suppressCellFocus: true,
  suppressContextMenu: true,
  suppressDragLeaveHidesColumns: true,
  tooltipInteraction: true,
};

export const defaultCompactTableProps = {
  ...defaultBaseTableProps,
  headerHeight: COMPACT_ROW_HEIGHT,
  rowHeight: COMPACT_ROW_HEIGHT,
};

export const defaultNarrowTableProps = {
  ...defaultBaseTableProps,
  headerHeight: NARROW_ROW_HEIGHT,
  rowHeight: NARROW_ROW_HEIGHT,
};

export enum Direction {
  asc = 'Ascending',
  desc = 'Descending',
}

export enum Order {
  asc = 'asc',
  desc = 'desc',
}

export enum FilterType {
  BOOLEAN = 'Boolean',
  DATE = 'Date',
  GUID = 'Guid',
  MULTI = 'Multi',
  NUMBER = 'Number',
  STRING_ARRAY = 'StringArray',
  TEXT = 'String',
}

export enum GridFilterType {
  DATE = 'date',
  SET = 'set',
}

export enum FilterOperationType {
  BETWEEN = 'Between',
  BLANK = 'Blank',
  CONTAINS = 'Contains',
  CONTAINS_ANY = 'ContainsAny',
  EMPTY = 'Empty',
  ENDS_WITH = 'EndsWith',
  EQUALS = 'Equals',
  GREATER_THAN = 'GreaterThan',
  GREATER_THAN_OR_EQUAL = 'GreaterThanOrEquals',
  IN_RANGE = 'InRange',
  LESS_THAN = 'LessThan',
  LESS_THAN_OR_EQUAL = 'LessThanOrEquals',
  MULTI = 'Multi',
  NOT_BLANK = 'NotBlank',
  NOT_CONTAINS = 'DoesNotContain',
  NOT_EQUAL = 'DoesNotEqual',
  NOT_IN_RANGE = 'NotInRange',
  STARTS_WITH = 'StartsWith',
}

// These filter operation types are used in ag-grid filters, and they differ from the ones used in our backend API, so we need to maintain a separate enum for them.
export enum GridFilterOperationType {
  BLANK = 'blank',
  CONTAINS = 'contains',
  EMPTY = 'empty',
  ENDS_WITH = 'endsWith',
  EQUALS = 'equals',
  GREATER_THAN = 'greaterThan',
  GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual',
  IN_RANGE = 'inRange',
  LESS_THAN = 'lessThan',
  LESS_THAN_OR_EQUAL = 'lessThanOrEqual',
  NOT_BLANK = 'notBlank',
  NOT_CONTAINS = 'notContains',
  NOT_EQUAL = 'notEqual',
  STARTS_WITH = 'startsWith',
}

export enum ColumnMenuItems {
  PIN_SUBMENU = 'pinSubMenu',
}
